<script setup lang="ts">
/**
 * One row of a notification, mention or saved-message feed: unread dot,
 * avatar (or an icon in `#icon`), title with a „×3“ fold counter, a clamped
 * excerpt, the source as a small chip and the time as „vor 5 Minuten“.
 *
 * The row is ONE click target — a link with `href`, else a button that emits
 * `open`. Row actions (`#actions`: mark read, remove, pin) sit next to it,
 * never inside, so they stay separate buttons; they show on hover and focus
 * and always on touch screens. What the row asks the reader to do
 * (`#primary-actions`: „Freigeben“, „Ablehnen“) sits below the text, outside
 * the click target too, and is always visible.
 *
 * Differs from `NxChatListItem`: that one is a presentational conversation
 * row (name, preview, counter) the host wraps in its own control; this one is
 * an event with its own click target, live time and row actions.
 */
import { Comment, Fragment, Text, computed, useTemplateRef, type VNode } from 'vue';
import NxAvatar from '../Avatar/NxAvatar.vue';
import NxCheckbox from '../Inputs/NxCheckbox.vue';
import NxRelativeTime from '../RelativeTime/NxRelativeTime.vue';
import type { NxDateLike } from '../RelativeTime/relativeTime';
import type { NxFeedAvatar } from './types';

const props = withDefaults(
  defineProps<{
    title: string;
    excerpt?: string;
    time?: NxDateLike;
    unread?: boolean;
    source?: string;
    /** Folded repeats; shown as „×3“ from 2 on. */
    count?: number;
    avatar?: NxFeedAvatar;
    /** Renders the row as a link. */
    href?: string;
    /** Spoken before the title of an unread row. */
    unreadLabel?: string;
    /** Relative-time locale; default: the app's. */
    locale?: string;
    /** A checkbox in front of the row for a multi-selection (`v-model:selected`). */
    selectable?: boolean;
    /** The checkbox's accessible name; default: the title. */
    selectLabel?: string;
  }>(),
  {
    excerpt: undefined,
    time: undefined,
    unread: false,
    source: undefined,
    count: undefined,
    avatar: undefined,
    href: undefined,
    unreadLabel: 'Ungelesen',
    locale: undefined,
    selectable: false,
    selectLabel: undefined,
  }
);

/** Whether the row is part of the selection; only read while `selectable`. */
const selected = defineModel<boolean>('selected', { default: false });

const emit = defineEmits<{
  /** The row was activated (also fires for a link, before the browser follows it). */
  open: [event: MouseEvent];
}>();

const slots = defineSlots<{
  /** Replaces the avatar, e.g. a module icon. */
  icon?(): unknown;
  /** Replaces the excerpt text, e.g. rendered mentions or a status badge; hidden when empty. */
  excerpt?(): unknown;
  /** Row actions next to the click target; shown on hover and focus. */
  actions?(): unknown;
  /**
   * Always visible actions below the text, e.g. „Freigeben“ / „Ablehnen“.
   * Left empty for a row (no actions), the area is not shown.
   */
  'primary-actions'?(): unknown;
}>();

/**
 * Whether a slot renders anything: a list passes its slot to every row, so
 * a row without a body or without actions gets one that renders nothing.
 */
function rendersContent(nodes: unknown): boolean {
  if (!Array.isArray(nodes)) return false;
  return nodes.some((node) => {
    const vnode = node as VNode;
    if (vnode.type === Comment) return false;
    if (vnode.type === Fragment) return rendersContent(vnode.children);
    if (vnode.type === Text) return String(vnode.children ?? '').trim() !== '';
    return true;
  });
}

const showCount = computed(() => (props.count ?? 0) > 1);
const hasMedia = computed(() => !!slots.icon || !!props.avatar);

const main = useTemplateRef<HTMLElement>('main');

defineExpose({
  /**
   * Moves focus to the row's click target (its button or link) — e.g. onto
   * the next row after the caller removed this one's neighbour.
   */
  focus(options?: FocusOptions) {
    main.value?.focus(options);
  },
});
</script>

<template>
  <div
    class="nx-feed-item"
    :class="{
      'is-unread': unread,
      'is-selected': selectable && selected,
      'has-media': hasMedia,
    }"
    data-testid="nx-feed-item"
  >
    <!-- Beside the click target like the actions: a button cannot hold a checkbox. -->
    <span v-if="selectable" class="nx-feed-item__select">
      <NxCheckbox v-model="selected" :aria-label="selectLabel ?? title" />
    </span>
    <div class="nx-feed-item__content">
      <div class="nx-feed-item__head">
        <component
          :is="href ? 'a' : 'button'"
          ref="main"
          class="nx-feed-item__main"
          :href="href"
          :type="href ? undefined : 'button'"
          @click="emit('open', $event)"
        >
          <span class="nx-feed-item__dot" aria-hidden="true" />
          <span v-if="hasMedia" class="nx-feed-item__media">
            <slot name="icon">
              <NxAvatar
                v-if="avatar"
                :name="avatar.name"
                :src="avatar.src"
                :initials="avatar.initials"
                :hue="avatar.hue"
                :shape="avatar.shape"
                :size="32"
                decorative
              />
            </slot>
          </span>
          <span class="nx-feed-item__body">
            <span class="nx-feed-item__title">
              <span v-if="unread" class="nx-feed-item__sr">{{ `${unreadLabel}: ` }}</span>
              {{ title }}
              <span v-if="showCount" class="nx-feed-item__count">{{ `×${count}` }}</span>
            </span>
            <span
              v-if="slots.excerpt ? rendersContent(slots.excerpt()) : excerpt"
              class="nx-feed-item__excerpt"
            >
              <slot name="excerpt">{{ excerpt }}</slot>
            </span>
            <span v-if="source || time" class="nx-feed-item__meta">
              <span v-if="source" class="nx-feed-item__source">{{ source }}</span>
              <NxRelativeTime
                v-if="time"
                class="nx-feed-item__time"
                :value="time"
                format="short"
                :locale="locale"
              />
            </span>
          </span>
        </component>
        <div v-if="$slots.actions" class="nx-feed-item__actions">
          <slot name="actions" />
        </div>
      </div>
      <!-- Below the text, outside the click target like the row actions. -->
      <div
        v-if="slots['primary-actions'] && rendersContent(slots['primary-actions']())"
        class="nx-feed-item__primary"
        data-testid="nx-feed-item-primary-actions"
      >
        <slot name="primary-actions" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.nx-feed-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  min-width: 0;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-lg);
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
/* Opaque row fills (the hover actions cover the time with the same fill). */
.nx-feed-item:hover {
  --feed-row-bg: var(--nxt-surface-sunken-bg);

  background: var(--feed-row-bg);
  border-color: var(--nxt-border-subtle);
}
.nx-feed-item.is-unread {
  --feed-row-bg: color-mix(in srgb, var(--nxt-brand-bg) 6%, var(--nxt-surface-bg));

  background: var(--feed-row-bg);
}
.nx-feed-item.is-unread:hover {
  --feed-row-bg: color-mix(in srgb, var(--nxt-brand-bg) 10%, var(--nxt-surface-bg));
}
.nx-feed-item.is-selected {
  border-color: var(--nxt-brand-border);
}

/* Sits on the first line of the title, like the unread dot. */
.nx-feed-item__select {
  flex: none;
  display: flex;
  padding-block-start: calc(var(--nxt-space-xs) * 1.25);
  padding-inline-start: calc(var(--nxt-space-xs) * 1.25);
}

/* The click target and the hover actions on top, the primary actions below. */
.nx-feed-item__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-radius: inherit;
}
/* The hover actions take the time's corner of this part, not of the row. */
.nx-feed-item__head {
  position: relative;
  display: flex;
  align-items: flex-start;
  min-width: 0;
  border-radius: inherit;
}

.nx-feed-item__main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: calc(var(--nxt-space-xs) * 1.25);
  margin: 0;
  padding: calc(var(--nxt-space-xs) * 1.25);
  border: 0;
  border-radius: inherit;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: start;
  text-decoration: none;
  cursor: pointer;
}
.nx-feed-item__main:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-feed-item__dot {
  flex: none;
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  margin-block-start: calc(var(--nxt-space-xs) * 0.875);
  border-radius: var(--nxt-radius-full);
  background: transparent;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-feed-item.is-unread .nx-feed-item__dot {
  background: var(--nxt-brand-bg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 3) var(--nxt-focus-ring-soft);
}

.nx-feed-item__media {
  flex: none;
  display: grid;
  place-items: center;
  min-width: var(--nxt-avatar-size-md);
  min-height: var(--nxt-avatar-size-md);
  color: var(--nxt-fg-subtle);
}

.nx-feed-item__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: calc(var(--nxt-space-2xs) * 0.75);
}
.nx-feed-item__title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-fg);
}
.nx-feed-item.is-unread .nx-feed-item__title {
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-feed-item__count {
  margin-inline-start: var(--nxt-space-2xs);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--nxt-fg-subtle);
}
.nx-feed-item__excerpt {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-subtle);
  overflow-wrap: anywhere;
}
.nx-feed-item__meta {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  margin-block-start: calc(var(--nxt-space-2xs) / 2);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
}
.nx-feed-item__source {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: calc(var(--nxt-space-2xs) / 2) calc(var(--nxt-space-xs) * 0.75);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-sunken-bg);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border-subtle);
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.02em;
}
.nx-feed-item__time {
  margin-inline-start: auto;
}

/* Starts under the text: past the click target's padding, the dot and the
   media column with their gaps. */
.nx-feed-item__primary {
  --feed-pad: calc(var(--nxt-space-xs) * 1.25);

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: 0 var(--feed-pad) var(--feed-pad) calc(var(--feed-pad) * 2 + var(--nxt-dot-size-md));
}
.nx-feed-item.has-media .nx-feed-item__primary {
  padding-inline-start: calc(
    var(--feed-pad) * 3 + var(--nxt-dot-size-md) + var(--nxt-avatar-size-md)
  );
}

/* Hover actions take the time's corner instead of reserving a column: the
   time stays flush right while nothing is hovered. */
.nx-feed-item__actions {
  position: absolute;
  inset-block-end: calc(var(--nxt-space-xs) * 0.75);
  inset-inline-end: calc(var(--nxt-space-xs) * 0.75);
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding-inline-start: var(--nxt-space-sm);
  border-radius: var(--nxt-radius-md);
  background: linear-gradient(
    to right,
    transparent,
    var(--feed-row-bg, var(--nxt-surface-bg)) var(--nxt-space-sm)
  );
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-feed-item:hover .nx-feed-item__actions,
.nx-feed-item:focus-within .nx-feed-item__actions {
  opacity: 1;
  pointer-events: auto;
}
@media (hover: none) {
  .nx-feed-item__actions {
    position: static;
    align-self: flex-start;
    padding: calc(var(--nxt-space-xs) * 0.75) calc(var(--nxt-space-xs) * 0.75) 0 0;
    background: none;
    opacity: 1;
    pointer-events: auto;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-feed-item,
  .nx-feed-item__dot,
  .nx-feed-item__actions {
    transition: none;
  }
}

.nx-feed-item__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
